import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator, IStorageLocal } from "namirasoft-core";
import { AskRow, AskStatus, ChatInferencerType, ChatRow, InteractionRow, InteractionStatus, NamirasoftInferenceMetaDatabase } from "namirasoft-inference";
import { NamirasoftMap } from "namirasoft-site-map";
import { IDFormatter, NSLoading } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { InferenceRouterProps } from "../InferenceRouterProps";
import { InferenceChatTargetType } from "../InferenceRoutes";
import Ask from "./Ask";
import ChatBar from "./ChatBar";
import Styles from "./ChatContent.module.css";
import ChatOptions, { ChatOptionsValue } from "./ChatOptions";
import InteractionGroup from "./InteractionGroup";

interface ChatContentProps extends NSARouterMakerResult, InferenceRouterProps
{
    chat_id: string;
    target_type?: InferenceChatTargetType;
    target_id?: string;
    showHeader?: boolean;
    showChatBar?: boolean;
    onNewChat?: (chat_id: string) => void;
}

interface ChatContentState
{
    chat: ChatRow | null;
    interactions: InteractionRow[];
    asks: AskRow[];
    loading: boolean;
    submitting: boolean;
}

function targetTypeLabel(target_type: InferenceChatTargetType): string
{
    switch (target_type)
    {
        case InferenceChatTargetType.LoadBalancer:
            return "Load Balancer";
        case InferenceChatTargetType.Failover:
            return "Failover";
        case InferenceChatTargetType.Race:
            return "Race";
        case InferenceChatTargetType.Router:
            return "Router";
        case InferenceChatTargetType.Gateway:
            return "Gateway";
        case InferenceChatTargetType.Model:
        default:
            return "Model";
    }
}

function inferencerTypeToTargetType(inferencer_type: ChatInferencerType): InferenceChatTargetType
{
    switch (inferencer_type)
    {
        case ChatInferencerType.LOAD_BALANCER:
            return InferenceChatTargetType.LoadBalancer;
        case ChatInferencerType.FAILOVER:
            return InferenceChatTargetType.Failover;
        case ChatInferencerType.RACE:
            return InferenceChatTargetType.Race;
        case ChatInferencerType.ROUTER:
            return InferenceChatTargetType.Router;
        case ChatInferencerType.GATEWAY:
            return InferenceChatTargetType.Gateway;
        case ChatInferencerType.MODEL:
        default:
            return InferenceChatTargetType.Model;
    }
}

function sortInteractions(rows: InteractionRow[]): InteractionRow[]
{
    return [...rows].sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at));
}

interface InteractionGroupData
{
    key: string;
    interactions: InteractionRow[];
    defaultIndex: number;
    sortKey: number;
}

function groupInteractions(rows: InteractionRow[]): InteractionGroupData[]
{
    let byRun = new Map<string, InteractionRow[]>();
    let groups: InteractionGroupData[] = [];
    for (let r of rows)
    {
        if (r.run_id)
        {
            let list = byRun.get(r.run_id);
            if (!list)
            {
                list = [];
                byRun.set(r.run_id, list);
            }
            list.push(r);
        }
        else
        {
            groups.push({
                key: r.id,
                interactions: [r],
                defaultIndex: 0,
                sortKey: +new Date(r.created_at),
            });
        }
    }
    for (let [run_id, list] of byRun.entries())
    {
        let ordered = [...list].sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at));
        let earliest = +new Date(ordered[0].created_at);
        let winnerIdx = ordered.findIndex(r => r.winner);
        if (winnerIdx > 0)
        {
            let [winner] = ordered.splice(winnerIdx, 1);
            ordered.unshift(winner);
        }
        groups.push({
            key: run_id,
            interactions: ordered,
            defaultIndex: 0,
            sortKey: earliest,
        });
    }
    return groups.sort((a, b) => a.sortKey - b.sortKey);
}

type ChatEntry =
    { key: string; sortKey: number; group: InteractionGroupData; ask: null } |
    { key: string; sortKey: number; group: null; ask: AskRow };

// A turn is an ask. It shows as its interactions when it produced any, and as the ask itself
// when it did not - a guard rail or a budget can reject the input before any model runs.
function buildEntries(interactions: InteractionRow[], asks: AskRow[]): ChatEntry[]
{
    let answered = new Set<string>();
    for (let i of interactions)
        if (i.ask_id_top_level)
            answered.add(i.ask_id_top_level);

    let entries: ChatEntry[] = groupInteractions(interactions).map(g => ({ key: g.key, sortKey: g.sortKey, group: g, ask: null }));
    for (let a of asks)
    {
        if (a.ask_id_parent)
            continue;
        if (answered.has(a.id))
            continue;
        // Rows that predate the status column: nothing is known about how they ended.
        if (!a.status)
            continue;
        entries.push({ key: a.id, sortKey: +new Date(a.created_at), group: null, ask: a });
    }
    return entries.sort((x, y) => x.sortKey - y.sortKey);
}

const POLL_INTERVAL_MS = 300;
const POLL_ACTIVITY_WINDOW_MS = 30000;

export default class ChatContent extends Component<ChatContentProps, ChatContentState>
{
    private ChatBar_Ref = createRef<ChatBar>();
    private ChatOptions_Ref = createRef<ChatOptions>();
    private ID_Formatter: IDFormatter;
    private pollTimer: ReturnType<typeof setTimeout> | null = null;
    private isMounted_ = false;
    private lastActivityAt: number = 0;

    constructor(props: ChatContentProps)
    {
        super(props);
        this.ID_Formatter = new IDFormatter(new NamirasoftMap(props.account.token_manager, console.error), new IStorageLocal());
        this.state = {
            chat: null,
            interactions: [],
            asks: [],
            loading: false,
            submitting: false,
        };
        this.onSubmit = this.onSubmit.bind(this);
        this.ask = this.ask.bind(this);
        this.pollTick = this.pollTick.bind(this);
    }

    override componentDidMount(): void
    {
        this.isMounted_ = true;
        this.load(true);
    }

    override componentWillUnmount(): void
    {
        this.isMounted_ = false;
        this.stopPolling();
    }

    override componentDidUpdate(prev: ChatContentProps): void
    {
        if (prev.chat_id !== this.props.chat_id
            || prev.target_id !== this.props.target_id
            || prev.target_type !== this.props.target_type)
        {
            this.stopPolling();
            this.load(true);
        }
    }

    private stopPolling()
    {
        if (this.pollTimer)
        {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    private schedulePoll()
    {
        if (!this.isMounted_ || this.pollTimer) return;
        this.pollTimer = setTimeout(this.pollTick, POLL_INTERVAL_MS);
    }

    private hasPendingInteractions(rows: InteractionRow[], asks: AskRow[]): boolean
    {
        if (rows.some(r => r.status === InteractionStatus.Running))
            return true;
        if (asks.some(a => a.status === AskStatus.Running))
            return true;
        if (this.lastActivityAt > 0 && Date.now() - this.lastActivityAt < POLL_ACTIVITY_WINDOW_MS)
            return true;
        return false;
    }

    private async pollTick()
    {
        this.pollTimer = null;
        if (!this.isMounted_ || !this.props.chat_id) return;
        try
        {
            let [rows, asks] = await this.fetchTurns(this.props.chat_id);
            if (!this.isMounted_) return;
            if (rows.length > this.state.interactions.length)
                this.lastActivityAt = 0;
            this.setState({ interactions: sortInteractions(rows), asks });
            if (this.hasPendingInteractions(rows, asks) || this.state.submitting)
                this.schedulePoll();
        }
        catch
        {
            if (this.isMounted_)
                this.schedulePoll();
        }
    }

    private async fetchInteractions(chat_id: string): Promise<InteractionRow[]>
    {
        let meta = new NamirasoftInferenceMetaDatabase();
        let filters = [new FilterItem(
            meta.tables.interaction,
            meta.tables.interaction.columns.chat_id,
            false,
            FilterItemOperator.all.equals,
            chat_id,
        )];
        let response = await this.props.servers.inference.interaction.List(filters, 1, 100, [], null);
        return response.rows;
    }

    private async fetchAsks(chat_id: string): Promise<AskRow[]>
    {
        let meta = new NamirasoftInferenceMetaDatabase();
        let filters = [new FilterItem(
            meta.tables.ask,
            meta.tables.ask.columns.chat_id,
            false,
            FilterItemOperator.all.equals,
            chat_id,
        )];
        let response = await this.props.servers.inference.ask.List(filters, 1, 100, [], null);
        return response.rows;
    }

    private async fetchTurns(chat_id: string): Promise<[InteractionRow[], AskRow[]]>
    {
        return await Promise.all([this.fetchInteractions(chat_id), this.fetchAsks(chat_id)]);
    }

    private async load(showLoading: boolean)
    {
        if (!this.props.chat_id)
        {
            this.setState({ chat: null, interactions: [], asks: [], loading: false });
            return;
        }
        if (showLoading) this.setState({ loading: true });
        try
        {
            let chat = await this.props.servers.inference.chat.Get(this.props.chat_id);
            let [rows, asks] = await this.fetchTurns(this.props.chat_id);
            if (!this.isMounted_) return;
            this.setState({
                chat,
                interactions: sortInteractions(rows),
                asks,
                loading: false,
            });
            if (this.hasPendingInteractions(rows, asks))
                this.schedulePoll();
        }
        catch
        {
            if (this.isMounted_)
                this.setState({ loading: false });
        }
    }

    private getEffectiveTarget(): { target_type: InferenceChatTargetType; target_id: string } | null
    {
        let target_type = this.props.target_type ?? (this.state.chat ? inferencerTypeToTargetType(this.state.chat.inferencer_type) : null);
        let target_id = this.props.target_id ?? this.state.chat?.inferencer_id ?? null;
        if (!target_type || !target_id)
            return null;
        return { target_type, target_id };
    }

    private onSubmit(message: string): boolean
    {
        let target = this.getEffectiveTarget();
        if (!target) return false;
        let options: ChatOptionsValue;
        try
        {
            options = this.ChatOptions_Ref.current?.getValue(true) ?? ChatOptions.EMPTY();
        }
        catch (error)
        {
            this.props.notifier.onError(error as Error);
            return false;
        }
        this.ask(target, message, options);
        return true;
    }

    private async ask(target: { target_type: InferenceChatTargetType; target_id: string }, message: string, options: ChatOptionsValue)
    {
        this.setState({ submitting: true });
        try
        {
            let body = {
                chat_id: this.props.chat_id || null,
                run_id: null,
                message,
                memory_limit: options.memory_limit,
                validator_id: options.validator_id,
                budget_ids: options.budget_ids,
                memory_ids: options.memory_ids,
                log_group_id: options.log_group_id,
                knowledgebase_ids: options.knowledgebase_ids,
                guardrail_ids: options.guardrail_ids,
                tags: options.tags,
            };
            let ack: { chat_id: string, run_id: string };
            switch (target.target_type)
            {
                case InferenceChatTargetType.LoadBalancer:
                    ack = await this.props.servers.inference.load_balancer.AskAsync(target.target_id, body);
                    break;
                case InferenceChatTargetType.Failover:
                    ack = await this.props.servers.inference.failover.AskAsync(target.target_id, body);
                    break;
                case InferenceChatTargetType.Race:
                    ack = await this.props.servers.inference.race.AskAsync(target.target_id, body);
                    break;
                case InferenceChatTargetType.Router:
                    ack = await this.props.servers.inference.router.AskAsync(target.target_id, body);
                    break;
                case InferenceChatTargetType.Gateway:
                    ack = await this.props.servers.inference.gateway.AskAsync(target.target_id, body);
                    break;
                case InferenceChatTargetType.Model:
                default:
                    ack = await this.props.servers.inference.model.AskAsync(target.target_id, body);
                    break;
            }

            this.lastActivityAt = Date.now();
            if (!this.props.chat_id)
            {
                this.setState({ submitting: false });
                this.props.onNewChat?.(ack.chat_id);
                return;
            }
            this.setState({ submitting: false });
            this.schedulePoll();
        }
        catch
        {
            this.setState({ submitting: false });
        }
    }

    override render()
    {
        let { showHeader = true, showChatBar } = this.props;
        let effectiveTarget = this.getEffectiveTarget();
        let canSubmit = effectiveTarget != null;
        let renderChatBar = showChatBar ?? canSubmit;

        let headerTargetType = effectiveTarget?.target_type ?? null;
        let headerTargetId = effectiveTarget?.target_id ?? null;
        let entries = buildEntries(this.state.interactions, this.state.asks);

        return (
            <div className={Styles.ns_chat_content}>
                {
                    showHeader && this.state.chat &&
                    <div className={Styles.ns_chat_header}>
                        <div className={Styles.ns_chat_header_title}>
                            {
                                headerTargetType && headerTargetId &&
                                <>
                                    <span className={Styles.ns_chat_header_item}>
                                        {targetTypeLabel(headerTargetType)}:&nbsp;{this.ID_Formatter.format(headerTargetId, null as any, null as any, false)}
                                    </span>
                                    <span className={Styles.ns_chat_separator}>-</span>
                                </>
                            }
                            <span className={Styles.ns_chat_header_item}>
                                Chat ID:&nbsp;{this.ID_Formatter.format(this.state.chat.id, null as any, null as any, false)}
                            </span>
                        </div>
                        <span>{entries.length} messages</span>
                    </div>
                }

                <div className={Styles.ns_chat_interactions}>
                    {
                        this.state.loading ?
                            <NSLoading title="Loading" description="Fetching interactions ..." />
                            :
                            entries.length === 0 && !this.state.submitting ?
                                <div className={Styles.ns_chat_empty}>
                                    <div className={Styles.ns_chat_empty_title}>What can I help with?</div>
                                </div>
                                :
                                entries.map(e =>
                                    e.group ?
                                        <InteractionGroup
                                            key={e.key}
                                            interactions={e.group.interactions}
                                            defaultIndex={e.group.defaultIndex}
                                        />
                                        :
                                        <Ask key={e.key} ask={e.ask} />
                                )
                    }
                    {
                        this.state.submitting &&
                        <NSLoading title="Submitting" description="Sending your message ..." />
                    }
                </div>

                {
                    renderChatBar && canSubmit &&
                    <div className={Styles.ns_chat_composer}>
                        <ChatOptions
                            ref={this.ChatOptions_Ref}
                            {...this.props}
                            disabled={this.state.submitting}
                        />
                        <ChatBar
                            ref={this.ChatBar_Ref}
                            placeholder="Ask anything..."
                            disabled={this.state.submitting || this.state.interactions.some(i => i.status === InteractionStatus.Running)}
                            onSubmit={this.onSubmit}
                        />
                    </div>
                }
            </div>
        );
    }
}